import React, { useEffect, useState } from 'react';
import { 
    Card, 
    Table, 
    Tag, 
    Button, 
    Select, 
    Space, 
    Typography, 
    Row, 
    Col, 
    Statistic,
    Input,
    message,
    Modal,
    Descriptions
} from 'antd';
import { 
    EyeOutlined, 
    SearchOutlined,
    UserOutlined,
    ClockCircleOutlined,
    CheckCircleOutlined,
    MessageOutlined,
    EditOutlined,
    CalculatorOutlined,
    SendOutlined
} from '@ant-design/icons';
import { useAuth } from '../../../contexts/AuthContext';
import api from '../../../services/api';
import pusherService from '../../../services/pusher';
import LeadEditModal from './LeadEditModal';
import EnhancedLeadReviewModal from './EnhancedLeadReviewModal';

const { Title, Text } = Typography;

function VendorLeads() {
    const { user } = useAuth();
    const [leads, setLeads] = useState([]);
    const [loading, setLoading] = useState(true);
    const [viewingLead, setViewingLead] = useState(null);
    const [modalVisible, setModalVisible] = useState(false);
    const [editingLead, setEditingLead] = useState(null);
    const [editModalVisible, setEditModalVisible] = useState(false);
    const [enhancedModalVisible, setEnhancedModalVisible] = useState(false);
    const [filters, setFilters] = useState({
        status: '',
        search: ''
    });

    const statusOptions = [
        { label: 'All Status', value: '' },
        { label: 'Pending', value: 'pending' },
        { label: 'Quoted', value: 'quoted' },
        { label: 'Accepted', value: 'accepted' },
        { label: 'Rejected', value: 'rejected' },
        { label: 'Completed', value: 'completed' }
    ];

    useEffect(() => {
        fetchLeads();
        
        // Set up Pusher notifications for new leads
        const setupPusher = async () => {
            try {
                await pusherService.onNewLead((data) => {
                    console.log('New lead received:', data);
                    message.success('New booking request received!');
                    fetchLeads(); // Refresh the leads list
                });
            } catch (error) {
                console.error('Failed to setup Pusher notifications:', error);
            }
        };
        
        setupPusher();
    }, [filters]);

    const fetchLeads = async () => {
        try {
            setLoading(true);
            const params = {
                per_page: 50,
                ...(filters.status && { status: filters.status }),
                ...(filters.search && { search: filters.search })
            };

            const response = await api.getVendorLeads(params);
            if (response.success) {
                setLeads(response.data.data || []);
            }
        } catch (error) {
            message.error('Failed to fetch leads');
        } finally {
            setLoading(false);
        }
    };

    const updateLeadStatus = async (leadId, status) => {
        try {
            await api.updateLead(leadId, { status });
            message.success('Lead status updated successfully');
            fetchLeads();
        } catch (error) {
            message.error('Failed to update lead status');
        }
    };

    const viewLeadDetails = (lead) => {
        setViewingLead(lead);
        setModalVisible(true);
    };

    const editLead = (lead) => {
        setEditingLead(lead);
        setEditModalVisible(true);
    };

    const openEnhancedReview = (lead) => {
        setEditingLead(lead);
        setEnhancedModalVisible(true);
    };

    const handleLeadSave = (updatedLead) => {
        setEditModalVisible(false);
        setEditingLead(null);
        fetchLeads(); // Refresh the list
    };

    const handleSendQuote = (quotedLead) => {
        setEditModalVisible(false);
        setEditingLead(null);
        fetchLeads(); // Refresh the list
        message.success('Quote sent successfully!');
    };

    const getStatusColor = (status) => {
        const colors = {
            pending: 'orange',
            quoted: 'blue',
            accepted: 'green',
            rejected: 'red',
            completed: 'green'
        };
        return colors[status] || 'default';
    };

    const columns = [
        {
            title: 'Lead ID',
            dataIndex: 'id',
            key: 'id',
            render: (id) => <Text strong>#{id}</Text>
        },
        {
            title: 'Customer',
            dataIndex: 'user',
            key: 'customer',
            render: (user) => (
                <div>
                    <div className="font-medium">{user?.name}</div>
                    <div className="text-sm text-gray-500">{user?.email}</div>
                </div>
            )
        },
        {
            title: 'Event Details',
            key: 'event',
            render: (_, record) => (
                <div>
                    <div className="font-medium">{record.event_type || 'Event'}</div>
                    <div className="text-sm text-gray-600">
                        {record.event_date} at {record.event_time}
                    </div>
                    <div className="text-sm text-gray-600">
                        {record.guest_count} guests
                    </div>
                </div>
            )
        },
        {
            title: 'Venue',
            dataIndex: 'venue',
            key: 'venue',
            ellipsis: true
        },
        {
            title: 'Status',
            dataIndex: 'status',
            key: 'status',
            render: (status) => (
                <Tag color={getStatusColor(status)}>
                    {status?.toUpperCase()}
                </Tag>
            )
        },
        {
            title: 'Actions',
            key: 'actions',
            render: (_, record) => (
                <Space>
                    <Button 
                        size="small" 
                        icon={<EyeOutlined />}
                        onClick={() => viewLeadDetails(record)}
                    >
                        View
                    </Button>
                    <Button 
                        size="small" 
                        icon={<EditOutlined />}
                        onClick={() => editLead(record)}
                        type="primary"
                    >
                        Edit
                    </Button>
                    <Button 
                        size="small" 
                        icon={<CalculatorOutlined />}
                        onClick={() => openEnhancedReview(record)}
                        style={{ backgroundColor: '#52c41a', borderColor: '#52c41a', color: 'white' }}
                    >
                        Review & Quote
                    </Button>
                    <Select
                        size="small"
                        value={record.status}
                        onChange={(value) => updateLeadStatus(record.id, value)}
                        style={{ minWidth: 120 }}
                    >
                        {statusOptions.slice(1).map(option => (
                            <Select.Option key={option.value} value={option.value}>
                                {option.label}
                            </Select.Option>
                        ))}
                    </Select>
                </Space>
            )
        }
    ];

    const stats = {
        total: leads.length,
        pending: leads.filter(l => l.status === 'pending').length,
        quoted: leads.filter(l => l.status === 'quoted').length,
        accepted: leads.filter(l => l.status === 'accepted').length
    };

    return (
        <div className="p-6">
            <div className="mb-6">
                <Title level={2}>Lead Management</Title>
                <Text type="secondary">Manage customer booking requests and leads</Text>
            </div>

            {/* Statistics */}
            <Row gutter={[16, 16]} className="mb-6">
                <Col xs={24} sm={6}>
                    <Card>
                        <Statistic
                            title="Total Leads"
                            value={stats.total}
                            prefix={<UserOutlined />}
                        />
                    </Card>
                </Col>
                <Col xs={24} sm={6}>
                    <Card>
                        <Statistic
                            title="Pending"
                            value={stats.pending}
                            valueStyle={{ color: '#faad14' }}
                        />
                    </Card>
                </Col>
                <Col xs={24} sm={6}>
                    <Card>
                        <Statistic
                            title="Quoted"
                            value={stats.quoted}
                            valueStyle={{ color: '#1890ff' }}
                        />
                    </Card>
                </Col>
                <Col xs={24} sm={6}>
                    <Card>
                        <Statistic
                            title="Accepted"
                            value={stats.accepted}
                            valueStyle={{ color: '#52c41a' }}
                        />
                    </Card>
                </Col>
            </Row>

            {/* Filters */}
            <Card className="mb-6">
                <Row gutter={[16, 16]} align="middle">
                    <Col xs={24} sm={12}>
                        <Input
                            placeholder="Search by customer name or event type"
                            prefix={<SearchOutlined />}
                            value={filters.search}
                            onChange={(e) => setFilters(prev => ({ ...prev, search: e.target.value }))}
                            allowClear
                        />
                    </Col>
                    <Col xs={24} sm={8}>
                        <Select
                            placeholder="Filter by status"
                            value={filters.status}
                            onChange={(value) => setFilters(prev => ({ ...prev, status: value }))}
                            options={statusOptions}
                            style={{ width: '100%' }}
                            allowClear
                        />
                    </Col>
                    <Col xs={24} sm={4}>
                        <Button 
                            onClick={() => setFilters({ status: '', search: '' })}
                            style={{ width: '100%' }}
                        >
                            Clear Filters
                        </Button>
                    </Col>
                </Row>
            </Card>

            {/* Leads Table */}
            <Card>
                <Table
                    columns={columns}
                    dataSource={leads}
                    loading={loading}
                    rowKey="id"
                    pagination={{
                        pageSize: 20,
                        showSizeChanger: true,
                        showQuickJumper: true,
                        showTotal: (total, range) => 
                            `${range[0]}-${range[1]} of ${total} leads`
                    }}
                    scroll={{ x: 1000 }}
                />
            </Card>

            {/* Lead Details Modal */}
            <Modal
                title="Lead Details"
                open={modalVisible}
                onCancel={() => setModalVisible(false)}
                footer={[
                    <Button key="close" onClick={() => setModalVisible(false)}>
                        Close
                    </Button>
                ]}
                width={800}
            >
                {viewingLead && (
                    <div className="space-y-4">
                        <Descriptions bordered column={2}>
                            <Descriptions.Item label="Lead ID">
                                <Typography.Text code>#{viewingLead.id}</Typography.Text>
                            </Descriptions.Item>
                            <Descriptions.Item label="Status">
                                <Tag color={getStatusColor(viewingLead.status)}>
                                    {viewingLead.status?.charAt(0).toUpperCase() + viewingLead.status?.slice(1)}
                                </Tag>
                            </Descriptions.Item>
                            <Descriptions.Item label="Customer">
                                {viewingLead.user?.name}
                            </Descriptions.Item>
                            <Descriptions.Item label="Email">
                                {viewingLead.user?.email}
                            </Descriptions.Item>
                            <Descriptions.Item label="Phone">
                                {viewingLead.contact_phone}
                            </Descriptions.Item>
                            <Descriptions.Item label="Event Type">
                                {viewingLead.event_type}
                            </Descriptions.Item>
                            <Descriptions.Item label="Event Date">
                                {viewingLead.event_date}
                            </Descriptions.Item>
                            <Descriptions.Item label="Event Time">
                                {viewingLead.event_time}
                            </Descriptions.Item>
                            <Descriptions.Item label="Guest Count">
                                {viewingLead.guest_count}
                            </Descriptions.Item>
                            <Descriptions.Item label="Venue">
                                {viewingLead.venue}
                            </Descriptions.Item>
                            <Descriptions.Item label="Special Requirements" span={2}>
                                {viewingLead.special_requirements || 'None'}
                            </Descriptions.Item>
                            <Descriptions.Item label="Selected Dishes" span={2}>
                                {viewingLead.selected_dishes ? (
                                    <div className="space-y-1">
                                        {viewingLead.selected_dishes.map((dish, index) => (
                                            <div key={index} className="flex justify-between">
                                                <span>{dish.name || `Dish ${index + 1}`}</span>
                                                <span className="text-gray-500">Qty: {dish.quantity || 1}</span>
                                            </div>
                                        ))}
                                    </div>
                                ) : 'None'}
                            </Descriptions.Item>
                        </Descriptions>
                    </div>
                )}
            </Modal>

            {/* Lead Edit Modal */}
            <LeadEditModal
                visible={editModalVisible}
                lead={editingLead}
                onClose={() => {
                    setEditModalVisible(false);
                    setEditingLead(null);
                }}
                onSave={handleLeadSave}
                onSendQuote={handleSendQuote}
            />

            {/* Enhanced Lead Review Modal */}
            <EnhancedLeadReviewModal
                visible={enhancedModalVisible}
                lead={editingLead}
                onClose={() => {
                    setEnhancedModalVisible(false);
                    setEditingLead(null);
                }}
                onSave={handleLeadSave}
                onSendQuote={handleSendQuote}
            />
        </div>
    );
}

export default VendorLeads;